import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CertifiedPayrollRow {
  line_id: string;
  company_id: string;
  personnel_project_id: string;
  week_ending: string;
  worker_id: string;
  employee_name: string;
  trade_classification: string | null;
  tax_id_last4: string | null;
  mon_hours: number;
  tue_hours: number;
  wed_hours: number;
  thu_hours: number;
  fri_hours: number;
  sat_hours: number;
  sun_hours: number;
  total_hours: number;
  regular_hours: number;
  ot_hours: number;
  hourly_rate: number;
  ot_rate: number;
  regular_wages: number;
  ot_wages: number;
  gross_wages: number;
  deductions: number;
  net_pay: number;
}

export function useCertifiedPayroll(personnelProjectId?: string, weekEnding?: string) {
  return useQuery<CertifiedPayrollRow[]>({
    queryKey: ["certified_payroll", personnelProjectId, weekEnding],
    queryFn: async () => {
      let query = supabase
        .from("v_certified_payroll" as any)
        .select("*")
        .eq("personnel_project_id", personnelProjectId!);

      if (weekEnding) {
        query = query.eq("week_ending", weekEnding);
      }

      const { data, error } = await query.order("employee_name");
      if (error) throw error;
      return (data ?? []) as unknown as CertifiedPayrollRow[];
    },
    enabled: !!personnelProjectId,
  });
}

export function useCertifiedPayrollWeeks(personnelProjectId?: string) {
  return useQuery<string[]>({
    queryKey: ["certified_payroll_weeks", personnelProjectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tm_timesheets")
        .select("week_ending")
        .eq("personnel_project_id", personnelProjectId!)
        .order("week_ending", { ascending: false });

      if (error) throw error;
      const unique = [...new Set((data ?? []).map((d) => d.week_ending))];
      return unique;
    },
    enabled: !!personnelProjectId,
  });
}

export function useSubmitCertifiedPayroll() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      companyId: string;
      projectId: string;
      personnelProjectId: string;
      weekEnding: string;
      notes?: string;
    }) => {
      const { error } = await supabase
        .from("certified_payroll_submissions")
        .upsert({
          company_id: params.companyId,
          project_id: params.projectId,
          personnel_project_id: params.personnelProjectId,
          week_ending: params.weekEnding,
          certification_statement: true,
          certified_by: user?.id,
          certified_at: new Date().toISOString(),
          notes: params.notes || null,
        }, { onConflict: "project_id,week_ending" });

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["certified_payroll"] });
    },
  });
}
